import { useState, useCallback, useEffect } from 'react';
import Pagenation from 'components/common/Pagenation';
import { afterServiceStateType, calculDayDateToString, formatDateToString, formattedDateTime } from 'pages/utils/commonUtils';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton } from 'components/common/Button';
import { AfterService } from 'type/type';
import { decryptAES256, getAfterServiceAll } from 'api/api';
import { CommonTable, CommonTableWrap, FilterContentContainer } from 'pages/Layout';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface SearchInputType {
  type: string,
  value: string,
  start_date: string,
  end_date: string,
  state?: number,
};

// 관리자 A/S 관리 목록
function AfterServiceList() {
  const navigate = useNavigate();

  // -------- 페이지네이션
  const buttonPerPage = 10; // 보여줄 페이지네이션 버튼 수
  const [page, setPage] = useState(1); // 현재 페이지
  const [limit] = useState(10); // 보여줄 게시물 개수
  const [totalCount, setTotalCount] = useState(-1);
  const numPages = Math.ceil(totalCount / limit); // 총 페이지 수
  const [startButtonNum, setStartButtonNum] = useState(1); // 버튼 시작 숫자
  const [loading, setLoading] = useState(true);
  
  // 페이지네이션 버튼 클릭 시
  const isClickButton = (str:string) => {
      if (str === 'prev') handlePrevPage();
      else handleNextPage();
  };

  // 이전페이지 이동(buttonPerPage만큼 -)
  const handlePrevPage = () => {
      const prevPage = Math.max(startButtonNum - buttonPerPage, 1);
      setStartButtonNum(prevPage);
      setPage(prevPage);
  };

  // 다음페이지 이동(buttonPerPage만큼 +)
  const handleNextPage = () => {
      const nextPage = Math.min(startButtonNum + buttonPerPage, numPages);
      setStartButtonNum(nextPage);
      setPage(nextPage);
  };

  // ----------- A/S 목록
  // 필터obj
  const [searchObj, setSearchObj] = useState<SearchInputType>({
    type: "user_id", value: "", start_date: calculDayDateToString(new Date(), -7), end_date: formatDateToString(new Date())
  });
  const [excelObj, setExcelObj] = useState<SearchInputType|null>(null);
  // A/S 리스트
  const [asList, setAsList] = useState<AfterService[]>([]);
  
  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') searchHandle();
  }


  const searchHandle = () => {
    if (page === 1) {
      getAfterServiceList(page, limit);
    } else {
      setStartButtonNum(1);
      setPage(1);
    }
  }

  // A/S 목록 조회
  const getAfterServiceList = useCallback(async (page: number, limit: number) => {
    const { type, value, start_date, end_date, state } = searchObj;
  
    setLoading(true);
  
    // state가 undefined이면 객체에서 제외
    const param = {
      [type]: value,
      start_date,
      end_date,
      ...(state !== undefined && { state }) // state가 있을 때만 추가
    };
  
    const asRes = await getAfterServiceAll(page, limit, param);
    if (asRes.code === 200) {
      setAsList(asRes.data);
      setTotalCount(asRes.total_count);
    }
    setExcelObj(searchObj);
    setLoading(false);
  }, [searchObj]);
  // 검색 type 변경
  const handleSearchType = (type: string) => {
    setSearchObj(prev => ({...prev, type, value: "" }));
  };
  // 검색 value 변경
  const handleSearchValue = (value: string) => {
    if (searchObj.type === "user_id") value = value.toLowerCase();
    setSearchObj(prev => ({...prev, value}));
  };
  // 검색 state 변경
  const handleSearchState = (value: number) => {
    setSearchObj(prev => {
      if (value > -1) {
        return { ...prev, state: value }; // 기존 로직
      } else {
        const { state, ...rest } = prev; // `state` 키만 제외하고 나머지 유지
        return rest;
      }
    });
  };
  // 검색 날짜 변경
  const handleSearchDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "search_start_date") setSearchObj(prev => ({ ...prev, start_date: value }));
    else setSearchObj(prev => ({...prev, end_date: value}));
  };

  // 엑셀 다운로드 버튼
  const handleExcelDownload = async () => {
    setLoading(true);
  
    if (!excelObj) return alert('먼저 조회 후 Excel 다운을 해주세요.');
    // state가 undefined이면 객체에서 제외
    const { type, value, start_date, end_date, state } = excelObj;
    const param = { 
      [type]: value, 
      start_date, 
      end_date, 
      ...(state !== undefined && { state }) 
    };
  
    // 1. 엑셀에 넣을 데이터 조회
    const asRes = await getAfterServiceAll(0, 0, param);
    if (asRes.code !== 200) {
      setLoading(false);
      return alert("Excel 변환에 실패하였습니다.");
    }
  
    const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
    const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
  
    // 2. 데이터 변환 및 암호 해독
    const formattedData = asRes.data.map((as: AfterService) => ({
      "No": as.idx,
      "ID": as.user_id,
      "이름": as.user_name,
      "연락처": decryptAES256(as.user_mobile, REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV),
      "E-Mail": as.user_email,
      "제목": as.title,
      "문의 내용": as.content,
      "설치위치": as.reg_name,
      "진행 상태": afterServiceStateType[as.state] || "알 수 없음", 
      "Model": as.model,
      "문의 유형": as.as_type,
      "답변 방법": as.answer_type,
      "생성일자": as.create_date,
      "수정일자": as.update_date,
      "답변 내용": as.answer?.content || "",
    }));
    
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('AfterService Data');

    // 헤더 설정
    worksheet.columns = [
      { header: 'No', key: 'No', width: 15 },
      { header: 'ID', key: 'ID', width: 20 },
      { header: '이름', key: '이름', width: 20 },
      { header: '연락처', key: '연락처', width: 20 },
      { header: 'E-Mail', key: 'E-Mail', width: 30 },
      { header: '제목', key: '제목', width: 30 },
      { header: '문의 내용', key: '문의 내용', width: 50 },
      { header: '설치위치', key: '설치위치', width: 20 },
      { header: '진행 상태', key: '진행 상태', width: 20 },
      { header: 'Model', key: 'Model', width: 15 },
      { header: '문의 유형', key: '문의 유형', width: 20 },
      { header: '답변 방법', key: '답변 방법', width: 20 },
      { header: '생성일자', key: '생성일자', width: 20 },
      { header: '수정일자', key: '수정일자', width: 20 },
      { header: '답변 내용', key: '답변 내용', width: 50 },
    ];

    // 데이터 추가
    formattedData.forEach((row: any) => {
      worksheet.addRow(row);
    });
    // 엑셀 파일로 저장
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'after_service_data.xlsx');
    });
    
    setLoading(false);
  }

  // 개별 처리페이지로 이동
  function processAfterService(serviceIdx: number) {
    navigate(`process/${serviceIdx}`);
  }
    
  useEffect(() => {
    if (!loading) getAfterServiceList(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);
  return (
    <>
      <FilterContentContainer>
        <div className='flex-row'>
          <label>검색</label>
          <div>
            <select name="search_type" onChange={(e) => handleSearchType(e.target.value)} value={searchObj.type} >
              <option value="user_id">고객 ID</option>
              <option value="title">제목</option>
              <option value="content">내용</option>
              <option value="user_name">고객명</option>
              <option value="reg_name">설치 위치</option>
              <option value="state">진행 상태</option>
            </select>
          </div>
          <div>
            <input type="text" name="search_value" onChange={(e) => handleSearchValue(e.target.value)} value={searchObj.value} placeholder="검색 내용 입력" onKeyUp={handleKeyDown} />
          </div>
        </div>
        <div className='flex-row'>
          <label>상태</label>
          <div>
            <select name="search_state" onChange={(e) => handleSearchState(Number(e.target.value))} value={searchObj.state} >
              <option value={-1}>전체</option>
              <option value={0}>대기</option>
              <option value={1}>진행중</option>
              <option value={2}>접수완료</option>
              <option value={3}>처리완료</option>
              <option value={4}>답변완료</option>
              <option value={5}>취소완료</option>
            </select>
          </div>
        </div>
        <div className='flex-row'>
          <label>등록일</label>
          <div className='flex-row'>
            <input type='date' name='search_start_date' value={searchObj.start_date} onChange={handleSearchDate} />
            <span>~</span>
            <input type='date' name='search_end_date' value={searchObj.end_date} onChange={handleSearchDate} />
          </div>
        </div>
        <PrimaryButton type="button" onClick={searchHandle}>조회</PrimaryButton>
        <PrimaryButton type="button" onClick={handleExcelDownload}>Excel 다운</PrimaryButton>
        <div className='flex-row btn-wrap'>
          <PrimaryButton type="button" onClick={() => processAfterService(0)}>등록</PrimaryButton>
        </div>
      </FilterContentContainer>
      <div>
        검색 결과: <span style={{ fontWeight: "600" }}>{totalCount < 0 ? 0 : totalCount}</span>건
      </div>
        <CommonTableWrap>
          <CommonTable>
            <thead>
              <tr>
                <th className='w-5'>No</th>
                <th className='w-10'>고객 ID</th>
                <th className='w-10'>고객명</th>
                <th className='w-15'>제목</th>
                <th className='w-15'>설치 위치</th>
                <th className='w-10'>구분</th>
                <th className='w-5'>상태</th>
                <th className='w-15'>등록일</th>
              </tr>
            </thead>
            <tbody>
              {totalCount === 0 ?
                <tr><td colSpan={8}>검색결과가 없습니다.</td></tr>
                :
                asList.map((as, idx) => 
                  <tr key={`as_${idx}`} onClick={() => processAfterService(as.idx)}>
                    <td className='w-5'>{(page - 1) * 10 + idx + 1}</td>
                    <td className='w-10 ellipsis-text'>{as.user_id}</td>
                    <td className='w-10 ellipsis-text'>{as.user_name}</td>
                    <td className='w-20 ellipsis-text'>{as.title}</td>
                    <td className='w-15 ellipsis-text'>{as.reg_name}</td>
                    <td className='w-10'>{as.as_type}</td>
                    <td className='state w-10' data-state={`state_${as.state}`}>
                      <span>{afterServiceStateType[as.state]}</span>
                    </td>
                    <td className='w-15'>{formattedDateTime(as.create_date)}</td>
                  </tr>
                )
              }
            </tbody>
          </CommonTable>
        </CommonTableWrap>
        <Pagenation
          totalCount={totalCount}
          total={numPages}
          limit={limit}
          page={page}
          setPage={setPage}
          startButtonNum={startButtonNum}
          setClickButton={isClickButton}
        />
    </>
  );
}

export default AfterServiceList;